import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useQuizProgress } from '@/features/survey/model/useQuizProgress'
import type { StyleQuizStep } from '../style-quiz.data'

/**
 * Style quiz — open / close and the resume point. Answers live on the server (saved at every
 * step, R203); the step to resume at and the number of outfits rated are the account's quiz
 * progress (`/user/quiz-progress/style`, BE-186) — no browser storage. Rarely is a real
 * frequency on the API since BE-151.
 */
export type StyleQuizEntry =
  /** From the top, or the step the user left at. */
  | { mode: 'resume' }
  /** Rate intro with these styles ticked (Style › Rate all). */
  | { mode: 'rate'; styleIds?: number[] }

/** The draft's keys are camelCase here, snake_case on the wire (the case interceptor). */
interface StyleQuizDraft extends Record<string, unknown> {
  rated?: number
}

export const useStyleQuizStore = defineStore('styleQuiz', () => {
  const isOpen = ref(false)
  const entry = ref<StyleQuizEntry>({ mode: 'resume' })
  /** Bumped on every open so the quiz re-reads the profile. */
  const session = ref(0)
  const progress = useQuizProgress<StyleQuizDraft>('style')
  const step = ref<StyleQuizStep | null>(null)
  const rated = ref(0)

  function open(next: StyleQuizEntry = { mode: 'resume' }) {
    entry.value = next
    session.value += 1
    isOpen.value = true
  }

  /** Style › Rate all / a style's "Rate": straight to Rate intro. */
  function openRate(styleIds?: number[]) {
    open({ mode: 'rate', styleIds })
  }

  function close() {
    isOpen.value = false
  }

  /** Reads the account's resume point (null step when nothing is saved). */
  async function loadProgress(): Promise<void> {
    const saved = await progress.load()
    step.value = (saved?.step as StyleQuizStep | null | undefined) ?? null
    rated.value = Number(saved?.answersDraft?.rated ?? 0) || 0
  }

  function savedStep(): StyleQuizStep | null {
    return step.value
  }

  function saveStep(next: StyleQuizStep) {
    // Welcome and Result are not places to come back to.
    step.value = next === 'result' || next === 'welcome' ? null : next
    void progress.save(step.value, { rated: rated.value })
  }

  function ratedCount(): number {
    return rated.value
  }

  function addRated() {
    rated.value += 1
    void progress.save(step.value, { rated: rated.value })
  }

  return {
    isOpen,
    entry,
    session,
    open,
    openRate,
    close,
    loadProgress,
    savedStep,
    saveStep,
    ratedCount,
    addRated,
  }
})
